import { toCalendarDateTime } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { format } from "date-fns";
import { EditProductionDeviationButton } from "features/CenterContent/RoleContent/ProductionDeviation/components/EditProductionDeviationButton";
import { ProductionDeviationResponse } from "features/CenterContent/RoleContent/ProductionDeviation/types/responseSchema";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import { TrainSerieDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainSerieDisplay";
import { TrainTypeDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainTypeDisplay";
import { productionDeviationQueryKey } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { CloseInformation } from "features/OperationalInformation/CloseInformation";
import { FC } from "react";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import { HStack, Stack } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { css, useTheme } from "styled-components";

type ProductionDeviationElementProps = {
  productionDeviation: ProductionDeviationResponse;
};

export const ProductionDeviationElement: FC<
  ProductionDeviationElementProps
> = ({ productionDeviation }) => {
  const theme = useTheme();
  const {
    reference,
    trainIds,
    eventType,
    updatedAt,
    fromDate,
    toDate,
    countryCode,
  } = productionDeviation;
  const durations = {
    fromTime: toCalendarDateTime(fromDate),
    toTime: toCalendarDateTime(toDate),
  };
  const closeMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/production-deviation/${reference}/close`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: productionDeviationQueryKey(countryCode),
      });
    },
  });
  return (
    <Stack key={reference}>
      <SegmentedPanel
        panelStyle={css`
          background: ${theme.colorBackgroundTertiary};
          border-radius: 12px;
          padding: 18px;
          gap: 18px;
        `}
        dashedLineVariant="tertiary"
      >
        <Stack>
          <HStack w="100%" justifyContent="space-between">
            <HStack>
              <TrainSerieDisplay trainIds={trainIds} />
              <TrainTypeDisplay eventType={eventType} />
            </HStack>
            <HStack>
              <CloseInformation closeMutation={closeMutation} />
              <EditProductionDeviationButton uuid={reference} />
            </HStack>
          </HStack>
          <HStack>
            <Badges eventCategory="productionDeviation" />
            <Badges eventCategory="train" />
          </HStack>
          <Badges displayDuration={durations} />
          <Text secondary>
            {format(
              updatedAt.toDate(),
              "'Sist oppdatert' dd.MM.yyyy 'kl.' HH:mm",
            )}
          </Text>
        </Stack>
      </SegmentedPanel>
    </Stack>
  );
};
